import { CACHE_MANAGER } from "../utils.js";
import { OPACITY_0_CSS_TEXT, OPACITY_1_CSS_TEXT } from "./Spinner.js";

export default class Renderer {
  /**
   * @param {import("./Spinner.js")} spinner
   */
  constructor(spinner) {
    this.spinner = spinner;
    this.frameThrottle = null;
    this.highQualityThrottle = null;
    this.viewerNode = spinner.viewerLayer.subject;
    this.onUpdateHandlers = [];
    this.fetchHighResImageThrottle = null;
    this.renderZoomLayerThrottle = null;
    this.renderViewerLayerThrottle = null;
  }

  get activeLayerType() {
    return this.spinner.activeLayer.type;
  }

  getHighResImageUrl(image) {
    const _spinner = this.spinner;

    return image.url || `${_spinner.imagesBaseUrl}/${image.hash}`;
  }

  /**
   *
   * @param {object} image
   * @returns {*}
   */
  getFrame(image) {
    const _spinner = this.spinner;
    const url = image.url || `${_spinner.imagesBaseUrl}/${image.hash}.1920`;

    if (CACHE_MANAGER.isReady(url)) {
      return CACHE_MANAGER.get(url);
    }

    clearTimeout(this.highQualityThrottle);
    this.highQualityThrottle = setTimeout(() => {
      CACHE_MANAGER.image(url).then(() => {
        if (_spinner.activeImage !== image) {
          return;
        }

        this.update(image);
      });
    }, 250);

    return CACHE_MANAGER.getOrFallback(
      image.url ||
        `${_spinner.imagesBaseUrl}/${image.hash}.${_spinner.initialImageSize}`,
    );
  }

  /**
   * Overwrite this function and add your business logic
   * @param {object} image
   * @param {Function} [callback]
   */
  update(image, callback) {
    cancelAnimationFrame(this.frameThrottle);
    this.frameThrottle = requestAnimationFrame(() => {
      const src = this.getFrame(image);
      this.viewerNode.src = src;

      requestAnimationFrame(() => {
        for (let i = 0, len = this.onUpdateHandlers.length; i < len; i++) {
          this.onUpdateHandlers[i](this.activeLayerType, src);
        }

        if (callback) callback();
      });
    });
  }

  /**
   * @param {Function} handler
   */
  addOnUpdate(handler) {
    this.onUpdateHandlers.push(handler);
  }

  reset() {
    this.onUpdateHandlers = [];
  }

  upscale() {
    const spinner = this.spinner;
    const image = spinner.activeImage;

    const highResolutionUrl = this.getHighResImageUrl(image);

    if (CACHE_MANAGER.isReady(highResolutionUrl)) {
      return this.renderHighResImage(
        image,
        CACHE_MANAGER.get(highResolutionUrl),
      );
    }

    CACHE_MANAGER.image(highResolutionUrl).then((cachedImageUrl) => {
      this.fetchHighResImage(image, cachedImageUrl);
    });
  }

  /**
   *
   * @param {object} image
   * @param {string} cachedImageUrl
   */
  renderHighResImage(image, cachedImageUrl) {
    const spinner = this.spinner;

    if (spinner.activeImage !== image) {
      return;
    }

    if (
      spinner.zoomLayerSubject.src === cachedImageUrl &&
      spinner.zoomLayerSubject.rendered
    ) {
      return;
    }

    this.abortUpscaling();
    spinner.zoomLayerSubject.src = cachedImageUrl;
    // We are using setTimeout to make sure there is no flickering
    this.renderZoomLayerThrottle = setTimeout(() => {
      spinner.zoomLayerSubject.rendered = true;
      spinner.zoomLayer.cssText += OPACITY_1_CSS_TEXT;

      this.renderViewerLayerThrottle = setTimeout(() => {
        spinner.viewerLayer.cssText += OPACITY_0_CSS_TEXT;
      });
    }, 500);
  }

  /**
   *
   * @param {object} image
   * @param {string} highResImageUrl
   */
  fetchHighResImage(image, highResImageUrl) {
    const spinner = this.spinner;
    /*
     * Since this method might be called multiple times in a short
     * for the same image, we can throttle it to improve performance
     */
    clearTimeout(this.fetchHighResImageThrottle);
    this.fetchHighResImageThrottle = setTimeout(() => {
      if (
        spinner.activeImage !== image ||
        spinner.zoomLayerSubject.src === highResImageUrl
      ) {
        return;
      }

      if (spinner.zoomScale <= 1.01) {
        image.highResolutionUrl = highResImageUrl;
        return;
      }

      image.highResolutionUrl = highResImageUrl;

      const imageLoader = new Image();
      imageLoader.decoding = "sync";
      imageLoader.onload = () => {
        this.renderHighResImage(image, highResImageUrl);
        spinner.dispatchEvent(
          new CustomEvent("frameupscale", {
            composed: true,
            detail: {
              image: image,
            },
          }),
        );
      };

      imageLoader.src = highResImageUrl;
    });
  }

  abortUpscaling() {
    clearTimeout(this.renderZoomLayerThrottle);
    clearTimeout(this.renderViewerLayerThrottle);
  }
}
